import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginCredentials, RegisterPayload } from '@/types/user.types';

/** Pick the `?return=…` query param and enforce a same-origin relative path. */
function safeReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null; // reject protocol-relative
  return raw;
}

export function useAuth() {
  const { user, isAuthenticated, isLoading, error, login, register, logout, fetchUser, clearError } =
    useAuthStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnUrl = safeReturnUrl(params.get('return'));

  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchUser();
    }
  }, [isAuthenticated, user, fetchUser]);

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      await login(credentials);
      navigate(returnUrl ?? '/dashboard');
    },
    [login, navigate, returnUrl],
  );

  const handleRegister = useCallback(
    async (payload: RegisterPayload) => {
      await register(payload);
      navigate(returnUrl ?? '/dashboard');
    },
    [register, navigate, returnUrl],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    clearError,
  };
}

export default useAuth;
